import React from 'react';
import { Link } from 'react-router-dom';
import Logo from '../components/Logo';

const HomePage: React.FC = () => {
  return (
    <div className="container">
      <div className="card patriotic-accent">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-lg)', marginBottom: 'var(--space-lg)', flexWrap: 'wrap' }}>
          <Logo size="large" />
          <h1 style={{ margin: 0 }}>Democracy Renaissance</h1>
        </div>
        <p style={{ fontSize: 'var(--font-size-lg)', textAlign: 'center', fontWeight: 500 }}>
          Promoting transparency and democratic accountability through access to government documents 
          and information that serves the public interest.
        </p>
      </div>

      <div className="card">
        <h2>Epstein Files Investigation</h2>
        <p>
          We have processed and made searchable over 22,000 pages of documents related to the Jeffrey Epstein case, 
          released by the House Oversight Committee. These documents provide crucial insights into one of the most 
          significant accountability cases of our time.
        </p>
        <p>
          Our platform makes these documents fully searchable using OCR technology, allowing researchers, 
          journalists, and the public to find specific information across thousands of pages.
        </p>
        <div style={{ 
          marginTop: '1.5rem', 
          display: 'flex', 
          gap: 'var(--space-md)', 
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}>
          <Link to="/documents" className="btn">
            Browse Documents
          </Link>
        </div>
      </div>

      <div className="card">
        <h2>How to Use This Site</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', marginTop: '1rem' }}>
          <div>
            <h3 style={{ color: '#007bff', marginBottom: '0.5rem' }}>Browse</h3>
            <p>Navigate through the document collection organized by file number and view page images with extracted text.</p>
          </div>
          <div>
            <h3 style={{ color: '#007bff', marginBottom: '0.5rem' }}>Search</h3>
            <p>Use Google-powered search to find specific terms, names, or topics across all document text.</p>
          </div>
          <div>
            <h3 style={{ color: '#007bff', marginBottom: '0.5rem' }}>Analyze</h3>
            <p>Cross-reference information across multiple documents to identify patterns and connections.</p>
          </div>
        </div>
      </div>

      <div className="card">
        <h2>About This Project</h2>
        <p>
          This initiative is part of the broader Democracy Renaissance project, which aims to strengthen 
          democratic institutions through transparency, accountability, and public access to information.
        </p>
        <p>
          We believe that an informed citizenry is essential for a functioning democracy. By making 
          important government documents easily accessible and searchable, we contribute to the public's 
          ability to hold powerful institutions accountable.
        </p>
        <p>
          <strong>Technical Implementation:</strong> This site is built as a static web application 
          hosted on AWS S3, ensuring fast, reliable access to the document archive. All document 
          processing was performed using advanced OCR technology to extract searchable text from 
          image files.
        </p>
      </div>

      <div className="card">
        <h2>Acknowledgments & Open Source</h2>
        <p>
          This project relies on exceptional open source technology to serve the public interest:
        </p>
        <div style={{ 
          backgroundColor: 'var(--democracy-light-blue)', 
          padding: 'var(--space-lg)', 
          borderRadius: '16px',
          border: '1px solid rgba(0, 40, 104, 0.1)',
          marginBottom: 'var(--space-lg)'
        }}>
          <h3 style={{ color: 'var(--democracy-blue)', marginBottom: 'var(--space-sm)' }}>
            🔍 Tesseract OCR
          </h3>
          <p style={{ margin: 0 }}>
            Document text extraction powered by{' '}
            <a 
              href="https://github.com/tesseract-ocr/tesseract" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ 
                color: 'var(--democracy-blue)', 
                fontWeight: 600,
                textDecoration: 'none',
                borderBottom: '1px solid var(--democracy-blue)'
              }}
            >
              Tesseract OCR
            </a>
            {' '}- An open source optical character recognition engine developed by Google. 
            This powerful tool enables us to make thousands of document pages searchable 
            and accessible to researchers and the public.
          </p>
        </div>
        <p style={{ fontStyle: 'italic', color: 'rgba(26, 31, 58, 0.7)' }}>
          We believe in transparency not just for government, but for the tools we use. 
          Open source software enables independent verification and community contribution 
          to democratic accountability projects.
        </p>
      </div>

      <div className="card">
        <h2>Data Sources & Methodology</h2>
        <p>
          <strong>Source:</strong> Documents were obtained from official House Oversight Committee releases 
          related to the Jeffrey Epstein investigation.
        </p>
        <p>
          <strong>Processing:</strong> Original document images were processed using Optical Character 
          Recognition (OCR) powered by{' '}
          <a 
            href="https://github.com/tesseract-ocr/tesseract" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ 
              color: 'var(--democracy-blue)', 
              textDecoration: 'none', 
              fontWeight: 600,
              borderBottom: '1px solid var(--democracy-blue)'
            }}
          >
            Tesseract OCR
          </a>{' '}
          to extract searchable text while preserving the original image format for verification.
        </p>
        <p>
          <strong>Organization:</strong> Files are organized by document number (HOUSE_OVERSIGHT_######) 
          and grouped into manageable batches for efficient browsing and search indexing.
        </p>
        <p>
          <strong>Search Technology:</strong> Google Site Search enables full-text search across all 
          extracted document content, providing comprehensive coverage of the entire archive.
        </p>
      </div>
    </div>
  );
};

export default HomePage;